export const enCommon = {
    app: {
        name: "Split Bill",
        tagline: "Smart expense sharing"
    },
    auth: {
        signIn: "Sign in",
        getStartedFree: "Get Started Free",
        noCreditCardRequired: "No credit card required"
    },
    language: {
        en: "English",
        vi: "Vietnamese"
    },
    footer: {
        feedback: "Feedback",
        copyright: "2025, Made with",
        by: "by Bùi Tiến Tuân"
    },
    components: {
        compactToggle: {
            ariaLabel: "Currency display unit",
            offHint: "Showing full amounts (1.000 ₫). Tap to switch to thousands (1 K).",
            onHint: "Showing thousands (1 K = 1.000 ₫). Tap to show full amounts.",
            unitDong: "in ₫",
            unitThousand: "in K (× 1,000 ₫)"
        },
        amountField: {
            hintFull: "Typing full amounts. Tap K to type in thousands instead.",
            hintThousand: "Typing in thousands: 3 = 3.000 ₫. Tap đ for full amounts."
        },
        userDropdown: {
            loggedIn: "Logged In",
            anonymousUser: "Anonymous User",
            notSignedIn: "Not signed in",
            actingAs: "Standing in for {{name}}",
            myProfile: "My Profile",
            eventProfile: "My Profile in This Event",
            myEvents: "My Events",
            feedbackManagement: "Feedback Management",
            eventManagement: "Event Management",
            userManagement: "User Management",
            eventHistory: "Event History",
            lightMode: "Light Mode",
            darkMode: "Dark Mode",
            logout: "Logout"
        },
        saveEventButton: {
            saving: "Saving...",
            saved: "Saved!",
            savedStatus: "Saved",
            saveToHistory: "Save to history",
            storageLimit: "Storage limit reached ({{current}}/{{max}} events)",
            removeFromHistory: "Click to remove from history",
            saveTooltip: "Save this event to view later in Event History"
        },
        userInfoModal: {
            bankName: "Bank Name",
            accountNumber: "Account Number",
            accountHolder: "Account Holder",
            noBankInfo: "No Banking Information",
            noBankInfoMessage: "{{participantName}} hasn't added their banking information yet",
            anonymousUser: "Anonymous User",
            anonymousUserMessage: "This user hasn't registered an account yet",
            bankingInformation: "Banking Information"
        },
        expenseList: {
            noExpenses: "No expenses yet",
            noExpensesMessage: "Add your first expense to get started",
            paidBy: "Paid by",
            splitBetween: "Split between",
            more: "and {{count}} more",
            you: "You",
            sponsor: "Sponsor",
            edit: "Edit",
            delete: "Delete",
            showAll: "Show all names",
            showLess: "Show less",
            eachPerson: "each",
            cannotEdit: "Cannot edit (time limit exceeded)",
            cannotDelete: "Cannot delete (time limit exceeded)",
            createdBy: "Created by",
            loadingName: "Loading name",
            dateGroup: {
                today: "Today",
                yesterday: "Yesterday",
                item: "item",
                items: "items"
            },
            moveUp: "Move up",
            moveDown: "Move down",
            dragHandle: "Hold to drag and reorder"
        },
        emptyState: {
            noExpensesYet: "No expenses yet",
            addExpensesToSee: "Add some expenses to see the settlement summary and balance calculations"
        },
        expenseForm: {
            title: "Add Expense",
            amount: "Amount",
            amountPlaceholder: "Enter amount",
            description: "Description",
            descriptionPlaceholder: "What was this expense for?",
            whoPaid: "Who Paid?",
            selectPayer: "Select payer",
            splitBetween: "Split Between",
            selectAll: "Select All",
            unselectAll:"Unselect All",
            adding: "Adding...",
            addExpense: "Add Expense",
            date: "Date",
            thousandMode: "Enter in thousands (×1.000)",
            amountPlaceholderThousand: "e.g. 3 = 3.000",
            help: {
                whoPaid: "Pick the person who actually paid this bill with their own money. When settling up, the app makes sure the others pay them back their share.",
                splitBetween: "Tick only the people who actually shared this expense — the amount is split equally among them, or by share ratio for anyone the event owner set one for (shown as % on their name). Untick anyone who didn't join (came late, skipped this meal...)."
            },
            errors: {
                fillAllFields: "Please fill in all fields",
                invalidAmount: "Please enter a valid amount greater than 0",
                addFailed: "Failed to add expense. Please try again."
            }
        },
        sponsorForm: {
            title: "Add Sponsorship",
            backToAddExpense: "Back to Add Expense",
            sponsorName: "Sponsor Name",
            sponsorNamePlaceholder: "e.g., ABC Company, John Doe...",
            amount: "Sponsorship Amount",
            amountPlaceholder: "Enter amount",
            date: "Date",
            thousandMode: "Enter in thousands (×1.000)",
            amountPlaceholderThousand: "e.g. 300 = 300.000",
            sponsorType: "Sponsor Type",
            externalSponsor: "External sponsor (from outside the group)",
            participantSponsor: "Participant sponsor (group member)",
            hasHolder: "Someone holds the sponsor money",
            whoHoldsMoney: "Who holds the money?",
            selectHolder: "Select holder",
            whoIsSponsor: "Who is sponsoring?",
            selectSponsorPayer: "Select sponsor",
            participantSponsorModeLabel: "Payment mode",
            payShareMode: "Still pay their share",
            payShareModeHint: "The sponsor pays the sponsorship amount, then still pays their own share of expenses like everyone else.",
            coverFirstMode: "Sponsor covers their share first",
            coverFirstModeHint: "The sponsor's amount covers their own share first. If sponsorship > their share, they don't pay extra.",
            beneficiaries: "Beneficiaries",
            allParticipants: "All participants",
            selectSpecificPeople: "Select specific people",
            selectAll: "Select All",
            adding: "Adding...",
            addSponsorship: "Add Sponsorship",
            help: {
                sponsorType: "External sponsor: the money comes from outside the group — a company, a boss, or someone who isn't a participant. Use this when the group receives support money.\n\nParticipant sponsor: a group member contributes extra money on top of splitting expenses like everyone else (e.g., the host chips in extra as a treat).",
                holder: "Tick this when a group member is keeping the sponsor's cash — usually the organizer who received it. When settling up, the app counts that this person must pass that money on to the beneficiaries.\n\nLeave it unticked if the sponsor money was already spent directly and nobody is holding it.",
                mode: "Decides how the sponsor's own share of the group's expenses is treated.\n\nExample: A sponsors 300K, and A's own share of the expenses is 250K.\n\n• Still pay their share: the whole 300K is an extra gift — every beneficiary (A included) gets an equal cost reduction, and A still pays their 250K share.\n\n• Cover their share first: the 300K pays A's 250K share first; only the remaining 50K is shared among the other beneficiaries. A never pays more than the 300K.",
                beneficiaries: "The people this sponsorship pays for — each one's cost is reduced by an equal share. Choose specific people when the sponsorship only covers part of the group (e.g., only the team members, not the guests)."
            },
            errors: {
                fillAllFields: "Please fill in all fields and select at least one beneficiary",
                selectHolder: "Please select who will hold the sponsor money",
                selectSponsorPayer: "Please select who is sponsoring",
                addFailed: "Failed to add sponsorship. Please try again."
            }
        },
        feedbackModal: {
            signInRequired: "Please sign in to submit feedback",
            fillAllFields: "Please fill in all fields",
            thankYou: "Thank you for your feedback!",
            helpImprove: "Help us improve",
            shareYourFeedback: "Share your feedback, report bugs, or suggest new features",
            feedbackType: "Feedback Type",
            feedbackTypes: {
                general: "General Feedback",
                bug: "Bug Report",
                feature: "Feature Request",
                ui: "UI/UX Feedback"
            },
            rateExperience: "Rate your experience",
            subject: "Subject",
            subjectPlaceholder: "Brief description of your feedback",
            message: "Message",
            messagePlaceholder: "Please describe your feedback, bug report, or feature request in detail...",
            submitting: "Submitting...",
            submitFeedback: "Submit Feedback",
            cancel: "Cancel"
        },
        authRequired: {
            authenticationRequired: "Authentication Required",
            defaultMessage: "Please sign in with Google to access this feature.",
            goToHome: "Go to Home"
        },
        appHeader: {
            backToHome: "Back to Home"
        },
        loadingSpinner: {
            logoAlt: "Split Bill Logo"
        },
        helpTip: {
            showHelp: "What does this mean?"
        }
    },
};

export const viCommon = {
    app: {
        name: "Chia Hóa Đơn",
        tagline: "Chia sẻ chi phí thông minh"
    },
    auth: {
        signIn: "Đăng nhập",
        getStartedFree: "Bắt Đầu Miễn Phí",
        noCreditCardRequired: "Không cần thẻ tín dụng"
    },
    language: {
        en: "Tiếng Anh",
        vi: "Tiếng Việt"
    },
    footer: {
        feedback: "Phản Hồi",
        copyright: "2025, Được tạo với",
        by: "bởi Bùi Tiến Tuân"
    },
    components: {
        compactToggle: {
            ariaLabel: "Đơn vị hiển thị số tiền",
            offHint: "Đang hiện số tiền đầy đủ (1.000 ₫). Bấm để rút gọn về nghìn (1 K).",
            onHint: "Đang hiện dạng nghìn (1 K = 1.000 ₫). Bấm để hiện số tiền đầy đủ.",
            unitDong: "đơn vị ₫",
            unitThousand: "đơn vị K (× 1.000 ₫)"
        },
        amountField: {
            hintFull: "Đang nhập đủ số. Bấm K để nhập theo nghìn cho nhanh.",
            hintThousand: "Đang nhập theo nghìn: gõ 3 = 3.000 ₫. Bấm đ để nhập đủ số."
        },
        userDropdown: {
            loggedIn: "Đã Đăng Nhập",
            anonymousUser: "Người Dùng Ẩn Danh",
            notSignedIn: "Chưa đăng nhập",
            actingAs: "Đang đóng vai {{name}}",
            myProfile: "Hồ Sơ Của Tôi",
            eventProfile: "Hồ Sơ Trong Sự Kiện Này",
            myEvents: "Sự Kiện Của Tôi",
            feedbackManagement: "Quản Lý Phản Hồi",
            eventManagement: "Quản Lý Sự Kiện",
            userManagement: "Quản Lý Người Dùng",
            eventHistory: "Lịch Sử Sự Kiện",
            lightMode: "Chế Độ Sáng",
            darkMode: "Chế Độ Tối",
            logout: "Đăng Xuất"
        },
        saveEventButton: {
            saving: "Đang lưu...",
            saved: "Đã lưu!",
            savedStatus: "Đã lưu",
            saveToHistory: "Lưu vào lịch sử",
            storageLimit: "Đã đạt giới hạn lưu trữ ({{current}}/{{max}} sự kiện)",
            removeFromHistory: "Nhấp để xóa khỏi lịch sử",
            saveTooltip: "Lưu sự kiện này để xem sau trong Lịch Sử Sự Kiện"
        },
        userInfoModal: {
            bankName: "Tên Ngân Hàng",
            accountNumber: "Số Tài Khoản",
            accountHolder: "Chủ Tài Khoản",
            noBankInfo: "Không Có Thông Tin Ngân Hàng",
            noBankInfoMessage: "{{participantName}} chưa thêm thông tin ngân hàng của họ",
            anonymousUser: "Người Dùng Ẩn Danh",
            anonymousUserMessage: "Người dùng này chưa đăng ký tài khoản",
            bankingInformation: "Thông Tin Ngân Hàng"
        },
        expenseList: {
            noExpenses: "Chưa có chi phí nào",
            noExpensesMessage: "Thêm chi phí đầu tiên để bắt đầu",
            paidBy: "Thanh toán bởi",
            splitBetween: "Chia cho",
            more: "và {{count}} người khác",
            you: "Bạn",
            sponsor: "Tài trợ",
            edit: "Chỉnh sửa",
            delete: "Xóa",
            showAll: "Xem đầy đủ tên",
            showLess: "Thu gọn",
            eachPerson: "mỗi người",
            cannotEdit: "Không thể chỉnh sửa",
            cannotDelete: "Không thể xóa",
            createdBy: "Tạo bởi",
            loadingName: "Đang tải tên",
            dateGroup: {
                today: "Hôm nay",
                yesterday: "Hôm qua",
                item: "mục",
                items: "mục"
            },
            moveUp: "Di chuyển lên",
            moveDown: "Di chuyển xuống",
            dragHandle: "Giữ để kéo sắp xếp"
        },
        emptyState: {
            noExpensesYet: "Chưa có chi phí nào",
            addExpensesToSee: "Thêm một số chi phí để xem tóm tắt thanh toán và tính toán số dư"
        },
        expenseForm: {
            title: "Thêm Chi Phí",
            amount: "Số Tiền",
            amountPlaceholder: "Nhập số tiền",
            description: "Mô Tả",
            descriptionPlaceholder: "Chi phí này dành cho gì?",
            whoPaid: "Ai Đã Trả?",
            selectPayer: "Chọn người trả",
            splitBetween: "Chia Cho",
            selectAll: "Chọn Tất Cả",
            unselectAll:"Bỏ Chọn Tất Cả",
            adding: "Đang thêm...",
            addExpense: "Thêm Chi Phí",
            date: "Ngày",
            thousandMode: "Nhập theo đơn vị nghìn (×1.000)",
            amountPlaceholderThousand: "VD: 3 = 3.000",
            help: {
                whoPaid: "Chọn người đã thực sự bỏ tiền túi trả cho khoản này. Khi tất toán, ứng dụng sẽ tính để những người còn lại trả lại phần của mình cho người đó.",
                splitBetween: "Chỉ tick những người thực sự tham gia khoản chi này — số tiền được chia đều cho họ, riêng ai được chủ sự kiện đặt tỉ lệ (hiện % cạnh tên) thì chia theo tỉ lệ đó. Bỏ tick người không tham gia (đến muộn, không dùng món này...)."
            },
            errors: {
                fillAllFields: "Vui lòng điền tất cả các trường",
                invalidAmount: "Vui lòng nhập số tiền hợp lệ lớn hơn 0",
                addFailed: "Không thể thêm chi phí. Vui lòng thử lại."
            }
        },
        sponsorForm: {
            title: "Thêm Tài Trợ",
            backToAddExpense: "Quay Lại Thêm Chi Phí",
            sponsorName: "Tên Nhà Tài Trợ",
            sponsorNamePlaceholder: "ví dụ: Công ty ABC, Nguyễn Văn A...",
            amount: "Số Tiền Tài Trợ",
            amountPlaceholder: "Nhập số tiền",
            date: "Ngày",
            thousandMode: "Nhập theo đơn vị nghìn (×1.000)",
            amountPlaceholderThousand: "VD: 300 = 300.000",
            sponsorType: "Loại Tài Trợ",
            externalSponsor: "Tài trợ từ bên ngoài (không phải thành viên nhóm)",
            participantSponsor: "Thành viên trong nhóm tài trợ",
            hasHolder: "Có người giữ tiền tài trợ",
            whoHoldsMoney: "Ai giữ tiền?",
            selectHolder: "Chọn người giữ",
            whoIsSponsor: "Ai tài trợ?",
            selectSponsorPayer: "Chọn người tài trợ",
            participantSponsorModeLabel: "Chế độ thanh toán",
            payShareMode: "Vẫn trả phần của mình",
            payShareModeHint: "Người tài trợ trả tiền tài trợ, sau đó vẫn phải trả phần chi phí của mình như những người khác.",
            coverFirstMode: "Tiền tài trợ trừ vào phần của mình trước",
            coverFirstModeHint: "Tiền tài trợ sẽ trừ vào phần phải trả của người tài trợ trước. Nếu tiền tài trợ > phần phải trả, họ không cần trả thêm.",
            beneficiaries: "Người Thụ Hưởng",
            allParticipants: "Tất cả người tham gia",
            selectSpecificPeople: "Chọn người cụ thể",
            selectAll: "Chọn Tất Cả",
            adding: "Đang thêm...",
            addSponsorship: "Thêm Tài Trợ",
            help: {
                sponsorType: "Tài trợ từ bên ngoài: tiền đến từ ngoài nhóm — công ty, sếp, hoặc người không tham gia sự kiện. Dùng khi nhóm nhận được tiền hỗ trợ.\n\nThành viên trong nhóm tài trợ: một người trong nhóm góp thêm tiền, ngoài phần chia bình thường của họ (ví dụ: chủ tiệc góp thêm để khao mọi người).",
                holder: "Tick khi có một thành viên đang cầm tiền tài trợ — thường là người tổ chức đứng ra nhận tiền. Khi tất toán, ứng dụng sẽ tính người này phải chuyển khoản tiền đó lại cho những người thụ hưởng.\n\nKhông tick nếu tiền tài trợ đã được chi tiêu trực tiếp, không ai đang giữ.",
                mode: "Quyết định phần chia của chính người tài trợ được tính thế nào.\n\nVí dụ: A tài trợ 300K, phần chia của A trong các khoản chi là 250K.\n\n• Vẫn trả phần của mình: toàn bộ 300K là khoản góp thêm — mỗi người thụ hưởng (gồm cả A) được giảm chi phí bằng nhau, và A vẫn trả phần 250K của mình.\n\n• Trừ vào phần của mình trước: 300K trả phần 250K của A trước; chỉ 50K còn lại chia cho những người thụ hưởng khác. A không bao giờ phải trả quá 300K.",
                beneficiaries: "Những người được khoản tài trợ này chi trả — mỗi người được giảm chi phí một phần bằng nhau. Chọn người cụ thể khi tài trợ chỉ dành cho một phần nhóm (ví dụ: chỉ thành viên đội, không tính khách mời)."
            },
            errors: {
                fillAllFields: "Vui lòng điền tất cả các trường và chọn ít nhất một người thụ hưởng",
                selectHolder: "Vui lòng chọn ai sẽ giữ tiền tài trợ",
                selectSponsorPayer: "Vui lòng chọn ai tài trợ",
                addFailed: "Không thể thêm tài trợ. Vui lòng thử lại."
            }
        },
        feedbackModal: {
            signInRequired: "Vui lòng đăng nhập để gửi phản hồi",
            fillAllFields: "Vui lòng điền tất cả các trường",
            thankYou: "Cảm ơn phản hồi của bạn!",
            helpImprove: "Giúp chúng tôi cải thiện",
            shareYourFeedback: "Chia sẻ phản hồi, báo cáo lỗi hoặc đề xuất tính năng mới",
            feedbackType: "Loại Phản Hồi",
            feedbackTypes: {
                general: "Phản Hồi Chung",
                bug: "Báo Cáo Lỗi",
                feature: "Yêu Cầu Tính Năng",
                ui: "Phản Hồi Giao Diện/UX"
            },
            rateExperience: "Đánh giá trải nghiệm của bạn",
            subject: "Tiêu Đề",
            subjectPlaceholder: "Mô tả ngắn gọn về phản hồi của bạn",
            message: "Tin Nhắn",
            messagePlaceholder: "Vui lòng mô tả chi tiết phản hồi, báo cáo lỗi hoặc yêu cầu tính năng của bạn...",
            submitting: "Đang gửi...",
            submitFeedback: "Gửi Phản Hồi",
            cancel: "Hủy"
        },
        authRequired: {
            authenticationRequired: "Yêu Cầu Xác Thực",
            defaultMessage: "Vui lòng đăng nhập bằng Google để truy cập tính năng này.",
            goToHome: "Về Trang Chủ"
        },
        appHeader: {
            backToHome: "Quay Về Trang Chủ"
        },
        loadingSpinner: {
            logoAlt: "Logo Chia Hóa Đơn"
        },
        helpTip: {
            showHelp: "Ý nghĩa của lựa chọn này"
        }
    }
};